import type { ICampaignIssue, IInitializeCampaign } from '~/modules/campaign';
import type { Campaign } from '~/modules/campaign/types';

import { useCampaignValidation, useHandleCampaign } from '~/modules/campaign-template';
import { useUpdateCampaignState } from '~/modules/campaign';
import { CAMPAIGN_STATUS } from '~/constants/campaign';
import { useCallback, useState } from 'react';

interface IInitializeCampaignUpdaterProps {
  initializeCampaign: IInitializeCampaign;
  isShowDiscard: boolean;
  handleSetCampaignData: (data?: Campaign) => void;
}

export interface IInitializeCampaignUpdater {
  isLoadingUpdate: boolean;
  campaignIssues: ICampaignIssue[] | undefined;
  handleUpdateCampaign: (options?: {
    campaign?: Campaign;
    allowRestartCampaign?: boolean;
  }) => Promise<void>;
  handleRefetchCampaignIssues: (campaign: Campaign) => void;
}

export const useInitializeCampaignUpdater = ({
  initializeCampaign,
  handleSetCampaignData,
}: IInitializeCampaignUpdaterProps): IInitializeCampaignUpdater => {
  const [isLoadingUpdate, setIsLoadingUpdate] = useState(false);

  const { onUpdateCampaign } = useHandleCampaign();
  const { handleResetCampaignIssues, handleRefetchCampaignIssues, campaignIssues } =
    useCampaignValidation();
  const { onUpdateCampaignState } = useUpdateCampaignState();

  const handleUpdateCampaign = useCallback(
    async (options?: { campaign?: Campaign; allowRestartCampaign?: boolean }) => {
      const campaignUpdate = options?.campaign ?? initializeCampaign.campaign;
      if (!campaignUpdate) return;

      try {
        setIsLoadingUpdate(true);
        handleRefetchCampaignIssues(campaignUpdate);
        if (options?.allowRestartCampaign) {
          await onUpdateCampaignState(
            { ...campaignUpdate, status: CAMPAIGN_STATUS.STOPPED },
            { hideSuccessMessage: true },
          );
        }
        // const issues = handleValidateCampaign(campaignUpdate);
        // if (issues.length) throw new Error(issues[0].message);

        const campaign = await onUpdateCampaign(campaignUpdate);
        // The restart can rewrite more than `status` server-side — a Price test re-pointed at another
        // product gets a fresh duplicate — and the store already holds that response, so the page has
        // to continue from it too or the save bar reads the gap as an unsaved change.
        const restartedCampaign = options?.allowRestartCampaign
          ? await onUpdateCampaignState(
              { ...campaignUpdate, status: CAMPAIGN_STATUS.RUNNING },
              { hideSuccessMessage: true },
            )
          : undefined;
        const campaignData = campaign
          ? { ...campaign, ...restartedCampaign, status: campaignUpdate?.status }
          : undefined;
        handleSetCampaignData(campaignData);
        handleResetCampaignIssues();
      } catch (error) {
        throw new Error((error as Error).message);
      } finally {
        setIsLoadingUpdate(false);
      }
    },
    // eslint-disable-next-line
    [
      initializeCampaign.campaign,
      onUpdateCampaign,
      handleResetCampaignIssues,
      handleRefetchCampaignIssues,
      handleSetCampaignData,
    ],
  );

  // useDebouncedEffect(
  //   () => {
  //     const { campaign } = initializeCampaign;

  //     if (!isShowDiscard) return;
  //     if (!campaign) return;
  //     if (campaign.status === CAMPAIGN_STATUS.RUNNING) return;

  //     const issues = handleValidateCampaign(campaign);
  //     if (issues.length) return;

  //     onUpdateCampaign(campaign, CAMPAIGN_MSG.AUTO_SAVE);
  //   },
  //   [initializeCampaign.campaign, isShowDiscard, handleValidateCampaign, onUpdateCampaign],
  //   DELAY_AUTO_SAVE,
  // );

  return {
    isLoadingUpdate,
    campaignIssues,
    handleUpdateCampaign,
    handleRefetchCampaignIssues,
  };
};
